/**
 * Events pushed right before the page leaves (window.location.href = Stripe,
 * Google sign-in) are lost: the browser unloads before GTM has sent the hit.
 * These helpers push with GTM's `eventCallback` + `eventTimeout` and leave
 * only in that callback, or after `timeoutMs` if GTM never answers (blocked
 * gtm.js) — exactly once either way. Without GTM on the page (any host but
 * torsunov.com, an iframe, tests) they push and leave at once.
 */
import { armNextEventPush, track } from "./dataLayer";
import type { EventParams } from "./dataLayer";

export const NAVIGATE_TIMEOUT_MS = 600;

type GtmWindow = Window & { dataLayer?: unknown[]; google_tag_manager?: unknown };

/** GTM is loaded, or its snippet ran and gtm.js is on the way. */
export const gtmOnPage = (): boolean => {
    try {
        const w = window as GtmWindow;
        if (w.google_tag_manager) return true;
        const dl = w.dataLayer;
        return (
            Array.isArray(dl) &&
            dl.some((e) => !!e && typeof e === "object" && (e as { event?: unknown }).event === "gtm.js")
        );
    } catch {
        return false;
    }
};

export type Destination = string | (() => void);

const leave = (to: Destination): void => {
    if (typeof to === "string") window.location.href = to;
    else to();
};

/**
 * Runs `push` (any helper that ends in one `track` / `trackEcommerce`, e.g.
 * `() => trackCheckoutRedirect(checkout)`), then goes to `to` once the event
 * is delivered. A `push` that pushes no event leaves at once. The promise
 * resolves right after `to` ran.
 */
export const pushThenNavigate = (
    push: () => unknown,
    to: Destination,
    timeoutMs: number = NAVIGATE_TIMEOUT_MS,
): Promise<void> =>
    new Promise<void>((resolve) => {
        let done = false;
        let timer: ReturnType<typeof setTimeout> | undefined;
        const go = (): void => {
            if (done) return;
            done = true;
            if (timer !== undefined) clearTimeout(timer);
            try {
                leave(to);
            } finally {
                resolve();
            }
        };
        const run = (): void => {
            try {
                push();
            } catch {
                // analytics must never block the navigation
            }
        };
        if (!gtmOnPage()) {
            run();
            go();
            return;
        }
        const disarm = armNextEventPush({ eventCallback: go, eventTimeout: timeoutMs });
        run();
        if (disarm()) {
            // nothing was pushed — nothing to wait for
            go();
            return;
        }
        if (!done) timer = setTimeout(go, timeoutMs);
    });

/** `track(event, params)`, then `to` once GTM has sent it (see above). */
export const trackThenNavigate = (
    event: string,
    params: EventParams | undefined,
    to: Destination,
    timeoutMs: number = NAVIGATE_TIMEOUT_MS,
): Promise<void> => pushThenNavigate(() => track(event, params), to, timeoutMs);
